import React, { useEffect, useRef, useState } from 'react';
import './RegistrationPage.css';
import confetti from 'canvas-confetti';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AsYouType,
  CountryCode,
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from 'libphonenumber-js';
import { getExampleNumber } from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.full.json';
import examples from 'libphonenumber-js/examples.mobile.json';
import { COUNTRY_OPTIONS } from '../utils/dialCodes';
import LoadingOverlay from '../components/LoadingOverlay';
import ColorWheelPicker, { GlowValue } from '../components/ColorWheelPicker';
import TermsAndConditionsLink from '../components/TermsAndConditionsLink';
import SmsConsentModal from '../components/SmsConsentModal';
import { useCustom } from '../hooks/useCustomization.js';
import { portalLoginUrl } from '../utils/portalLoginUrl';

const COUNTRY_MAP = new Map(COUNTRY_OPTIONS.map((c) => [c.code, c]));

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

interface InvitePrefill {
  validated: boolean;
  email: string;
  companyCode: string;
  legalCompanyName: string;  // used in the invite email only
  platformName: string;      // shown in the masked company code field
  openJoin?: boolean;        // public open self-join (e.g. CaddieGames): email stays editable
  isCaddieGames?: boolean;   // org being registered for is CaddieGames — changes the T&C link
  isMapleCoin?: boolean;     // org being registered for is MapleCoin — post-signup login goes to its portal
  existingUser?: boolean;    // invited email already has an account → join-org flow, not registration
}

enum FormStage {
  Landing = 'Landing',
  VerifyEmail = 'VerifyEmail',
  EnterMobile = 'EnterMobile',
  VerifyMobile = 'VerifyMobile',
  Password = 'Password',
  Customization = 'Customization',
  Success = 'Success',
  ExistingAccount = 'ExistingAccount',
}

export default function RegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setCustomization } = useCustom();

  const planData = searchParams.get('plan') || 'trial';

  // ── Stage ────────────────────────────────────────────────────────────────
  const [stage, setStage] = useState<FormStage>(FormStage.Landing);
  const [isOrg, setIsOrg] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  // ── Landing fields ───────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companyIdentifier, setCompanyIdentifier] = useState('');

  // ── OTP inputs ───────────────────────────────────────────────────────────
  const [emailOtp, setEmailOtp] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [emailResendCountdown, setEmailResendCountdown] = useState(0);
  const [phoneResendCountdown, setPhoneResendCountdown] = useState(0);

  // ── Phone (EnterMobile stage) ────────────────────────────────────────────
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_OPTIONS[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const digitsRef = useRef('');
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // ── SMS consent (Twilio A2P 10DLC) ───────────────────────────────────────
  // Never pre-accepted: the modal opens closed-and-unconfirmed on every visit,
  // and no SMS is requested until "Agree & Send Code" is clicked.
  const [showSmsConsent, setShowSmsConsent] = useState(false);
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const consentSubmittingRef = useRef(false);

  // ── Password stage ───────────────────────────────────────────────────────
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Customization stage (org path) ───────────────────────────────────────
  const [companyName, setCompanyName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [colour, setColour] = useState('#218838');
  const [textColour, setTextColour] = useState('#ffffff');
  const [glow, setGlow] = useState<GlowValue>({ color: '#000000', intensity: 0 });
  const [orgTermsAccepted, setOrgTermsAccepted] = useState(false);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [showExistingPopup, setShowExistingPopup] = useState(false);

  // ── Invite state ─────────────────────────────────────────────────────────
  const [isInvited, setIsInvited] = useState(false);
  const [isOpenJoin, setIsOpenJoin] = useState(false);
  const [isCaddieGames, setIsCaddieGames] = useState(false);
  const [isMapleCoin, setIsMapleCoin] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  const [invitedOrgName, setInvitedOrgName] = useState('');

  // Live banner preview — only active during Customization and the Success screen
  useEffect(() => {
    if (stage !== FormStage.Customization && stage !== FormStage.Success) return;
    setCustomization({
      branding: {
        headerBgColor: colour,
        headerTxtColor: textColour,
        title: displayName || 'AskSmart',
        glow,
      },
      orgID: '',
    });
  }, [stage, colour, textColour, glow, displayName, setCustomization]);

  // Resend countdown timers
  useEffect(() => {
    if (emailResendCountdown <= 0) return;
    const id = setTimeout(() => setEmailResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [emailResendCountdown]);

  useEffect(() => {
    if (phoneResendCountdown <= 0) return;
    const id = setTimeout(() => setPhoneResendCountdown((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [phoneResendCountdown]);

  // Validate invite token on first landing — no-op when no token present
  useEffect(() => {
    const token:string|null = searchParams.get('inviteToken');
    if (!token) return;

    async function verifyInviteToken() {
      setLoading(true);
      try {
        const data: InvitePrefill = await apiPost('validate-invite', { token });
        // Existing AskSmart account → reuse the "Join Another Organization"
        // flow (password + join) instead of new-account registration/OTP. The
        // invite token carries the org, so no company code is needed.
        if (data.existingUser) {
          navigate(`/existing/user?inviteToken=${encodeURIComponent(token!)}`, { replace: true });
          return;
        }
        setInviteToken(token!);
        setEmail(data.email);
        setCompanyIdentifier(data.companyCode);
        setInvitedOrgName(data.platformName || data.legalCompanyName);
        setIsInvited(true);
        setIsOpenJoin(data.openJoin === true);
        setIsCaddieGames(data.isCaddieGames === true);
        setIsMapleCoin(data.isMapleCoin === true);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    verifyInviteToken();
  }, []);

  // ── API helper ───────────────────────────────────────────────────────────
  async function apiPost(path: string, body: object): Promise<any> {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/register/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Something went wrong.');
    return data;
  }

  // ── Stage: Landing ───────────────────────────────────────────────────────
  async function handleLandingSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const data = await apiPost('initiate', {
        firstName,
        lastName,
        email,
        companyIdentifier: companyIdentifier || undefined,
        ...(isInvited && { invited: true, inviteToken }),
      });
      // Open-join brand + an email that already has an account: the backend has
      // emailed a "use your existing credentials" link instead of an OTP. Show
      // the check-your-email screen rather than the verification-code step.
      if (data.existingAccount) {
        setStage(FormStage.ExistingAccount);
        return;
      }
      setIsOrg(data.isOrg);
      setMfaEnabled(data.mfaEnabled);
      setEmailResendCountdown(60);
      setStage(FormStage.VerifyEmail);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Stage: VerifyEmail ───────────────────────────────────────────────────
  async function handleEmailOtpChange(val: string) {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setEmailOtp(digits);
    setError('');
    if (digits.length < 6) return;
    setLoading(true);
    try {
      await apiPost('verify-email', { otp: digits });
      const nextStage = !isOrg && mfaEnabled ? FormStage.EnterMobile : FormStage.Password;
      setStage(nextStage);
    } catch (err: any) {
      setError(err.message);
      setEmailOtp('');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendEmail() {
    if (emailResendCountdown > 0) return;
    setError('');
    try {
      await apiPost('resend-email-otp', {});
      setEmailResendCountdown(60);
    } catch (err: any) {
      setError(err.message);
    }
  }

  // ── Stage: EnterMobile ───────────────────────────────────────────────────
  function formatNumber(digits: string, countryCode: CountryCode) {
    return new AsYouType(countryCode).input(digits);
  }

  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    const example = getExampleNumber(selectedCountry.code as CountryCode, examples, metadata);
    const maxLength = example?.nationalNumber?.length || 10;
    const limited = digitsOnly.slice(0, maxLength);
    digitsRef.current = limited;
    const formatted = formatNumber(limited, selectedCountry.code as CountryCode);
    setPhoneDisplay(formatted);
    setPhoneNumber(`+${getCountryCallingCode(selectedCountry.code as CountryCode)}${limited}`);
  }

  function handlePhoneKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Backspace') return;
    e.preventDefault();
    const newDigits = digitsRef.current.slice(0, -1);
    digitsRef.current = newDigits;
    const formatted = formatNumber(newDigits, selectedCountry.code as CountryCode);
    setPhoneDisplay(formatted);
    setPhoneNumber(`+${getCountryCallingCode(selectedCountry.code as CountryCode)}${newDigits}`);
    setTimeout(() => {
      phoneInputRef.current?.setSelectionRange(formatted.length, formatted.length);
    }, 0);
  }

  // "Send Code" no longer sends: it validates the number and opens the consent
  // modal. The SMS is only requested once the user clicks "Agree & Send Code".
  function handleMobileSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const parsed = parsePhoneNumberFromString(phoneNumber);
    if (!parsed?.isValid()) {
      setError('Please enter a valid phone number.');
      return;
    }
    setShowSmsConsent(true);
  }

  async function handleSmsConsentAgree() {
    // Guard against double-clicks: the ref flips synchronously, before React
    // re-renders the disabled button.
    if (consentSubmittingRef.current) return;
    consentSubmittingRef.current = true;

    setSmsConsentAccepted(true);
    setShowSmsConsent(false);
    setError('');
    setLoading(true);
    try {
      await apiPost('send-mobile-otp', { phoneNumber, smsConsent: true });
      setPhoneResendCountdown(60);
      setStage(FormStage.VerifyMobile);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      consentSubmittingRef.current = false;
    }
  }

  // ── Stage: VerifyMobile ──────────────────────────────────────────────────
  async function handlePhoneOtpChange(val: string) {
    const digits = val.replace(/\D/g, '').slice(0, 6);
    setPhoneOtp(digits);
    setError('');
    if (digits.length < 6) return;
    setLoading(true);
    try {
      await apiPost('verify-mobile', { otp: digits });
      setStage(FormStage.Password);
    } catch (err: any) {
      setError(err.message);
      setPhoneOtp('');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendPhone() {
    if (phoneResendCountdown > 0) return;
    // The consent already given covers codes the user explicitly re-requests;
    // without it there is nothing to resend.
    if (!smsConsentAccepted) return;
    setError('');
    try {
      await apiPost('send-mobile-otp', { phoneNumber });
      setPhoneResendCountdown(60);
    } catch (err: any) {
      setError(err.message);
    }
  }

  // ── Stage: Password ──────────────────────────────────────────────────────
  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!PASSWORD_REGEX.test(password)) {
      setError('Password must be at least 8 characters and include an uppercase letter, a number, and a special character.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!isOrg && !termsAccepted) {
      setError('You must accept the terms and conditions.');
      return;
    }
    if (isOrg) {
      setStage(FormStage.Customization);
      return;
    }
    setLoading(true);
    try {
      await apiPost('complete-user', {
        password,
        termsAccepted,
        ...(isInvited && { invited: true, inviteToken }),
      });
      setPassword('');
      setConfirmPassword('');
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setStage(FormStage.Success);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Stage: Customization ─────────────────────────────────────────────────
  async function handleCustomizationSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!companyName || !legalName || !displayName) {
      setError('Please fill in all organization details.');
      return;
    }
    if (!orgTermsAccepted) {
      setError('You must accept the terms and conditions.');
      return;
    }
    setLoading(true);
    try {
      await apiPost('complete-org', {
        password,
        termsAccepted: orgTermsAccepted,
        companyName,
        legalName,
        displayName,
        bannerColour: colour,
        bannerTextColour: textColour,
        // Only send a glow when it's actually on; default is no glow at all.
        ...(glow.intensity > 0 ? { glow } : {}),
        plan: planData,
        ...(isInvited && { invited: true, inviteToken }),
      });
      setPassword('');
      setConfirmPassword('');
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setStage(FormStage.Success);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Eye icon SVG ─────────────────────────────────────────────────────────
  function EyeIcon({ visible }: { visible: boolean }) {
    return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {visible ? (
          <>
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
            <circle cx="12" cy="12" r="3" />
          </>
        ) : (
          <>
            <path d="M1 1l22 22" />
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
            <circle cx="12" cy="12" r="3" />
          </>
        )}
      </svg>
    );
  }

  // ── Existing-account screen (open-join brand) ─────────────────────────────
  // The email already has an account, so we emailed a "use your existing
  // credentials" link instead of starting a fresh registration.
  if (stage === FormStage.ExistingAccount) {
    const brandName = isMapleCoin ? 'MapleCoin' : 'CaddieGames';
    return (
      <div className="registration-success-container">
        <div className="registration-success-card">
          <div className="registration-success-icon">✉</div>
          <h1>Check your email</h1>
          <p className="registration-success-lead">
            We've emailed <strong>{email}</strong> with a link to sign in to
            {brandName} using your existing AskSmart credentials. Please click the
            link in the email to continue.
          </p>
          <button
            className="back-to-login-btn"
            onClick={() => { window.location.href = isMapleCoin ? portalLoginUrl({ isMapleCoin }) : 'https://caddiegames.com'; }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ── Success screen ───────────────────────────────────────────────────────
  if (stage === FormStage.Success) {
    return (
      <div className="registration-success-container">
        <div className="registration-success-card">
          <div className="registration-success-icon">✓</div>
          {isOrg ? (
            <>
              <h1>Organization Created!</h1>
              <p className="registration-success-lead">
                Your organization and account are ready. Verify your email then log in.
              </p>
            </>
          ) : (
            <>
              <h1>You're all signed up!</h1>
              <p className="registration-success-lead">
                {isInvited
                  ? 'Your account is ready. You can log in now.'
                  : 'Two things still need to happen before you can log in:'}
              </p>
              {!isInvited && (
                <ol className="registration-success-steps">
                  <li>
                    <strong>Verify your email.</strong> Check your inbox (and spam folder) for a
                    verification message from <strong>AskSmart</strong> and click the link inside.
                  </li>
                  <li>
                    <strong>Wait for admin approval.</strong> An administrator from your organization
                    needs to approve your access. You'll receive a confirmation email from{' '}
                    <strong>AskSmart</strong> once you're approved.
                  </li>
                </ol>
              )}
            </>
          )}
          <button
            className="back-to-login-btn"
            onClick={() => {
              // Portal invitees (CaddieGames, MapleCoin) log in via their brand's
              // per-env login URL, which drives that brand's login flow.
              window.location.href = portalLoginUrl({ isCaddieGames, isMapleCoin });
            }}
          >
            Go to Log In
          </button>
        </div>
      </div>
    );
  }

  // ── Main card ────────────────────────────────────────────────────────────
  return (
    <div className="registration-page-container">
      <LoadingOverlay show={loading} message="Please wait..." />
      <div className="registration-form-user">

        {/* ── Landing ─────────────────────────────────────────────────────── */}
        {stage === FormStage.Landing && (
          <form className="reg-stage-form" onSubmit={handleLandingSubmit} autoComplete="off">
            <h2>I'm here to help!</h2>
            {!isInvited && (
              <p
                className="signup-link"
                onClick={() => setShowExistingPopup(true)}
              >
                Already have an account?
              </p>
            )}
            <input
              type="text"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => (!isInvited || isOpenJoin) && setEmail(e.target.value)}
              readOnly={isInvited && !isOpenJoin}
              className={isInvited && !isOpenJoin ? 'reg-field-locked' : undefined}
              required
            />
            <input
              type="text"
              placeholder="Company Code (Leave blank for new account)"
              value={isInvited ? invitedOrgName : companyIdentifier}
              onChange={(e) => !isInvited && setCompanyIdentifier(e.target.value)}
              readOnly={isInvited}
              className={isInvited ? 'reg-field-locked' : undefined}
            />
            {error && <div className="error-message">{error}</div>}
            <div className="button-group">
              <button type="submit" disabled={loading} className="registrationLoginBtn">
                <span className="invisible-placeholder">Continue</span>
                <span className="button-content">Continue</span>
              </button>
            </div>
          </form>
        )}

        {/* ── Verify Email ─────────────────────────────────────────────────── */}
        {stage === FormStage.VerifyEmail && (
          <div className="reg-stage-form">
            <h2>Verify your email</h2>
            <p className="reg-stage-subtext">
              We sent a 6-digit code to <strong>{email}</strong>. Enter it below.
            </p>
            <input
              className="reg-otp-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="——————"
              maxLength={6}
              value={emailOtp}
              onChange={(e) => handleEmailOtpChange(e.target.value)}
              disabled={loading}
              autoFocus
            />
            {error && <div className="error-message">{error}</div>}
            <div className="reg-resend-text">
              {emailResendCountdown > 0 ? (
                <span className="reg-resend-countdown">Resend in {emailResendCountdown}s</span>
              ) : (
                <span className="reg-resend-link" onClick={handleResendEmail}>Resend code</span>
              )}
            </div>
          </div>
        )}

        {/* ── Enter Mobile ─────────────────────────────────────────────────── */}
        {stage === FormStage.EnterMobile && (
          <form className="reg-stage-form" onSubmit={handleMobileSubmit} autoComplete="off">
            <h2>Mobile number</h2>
            <p className="reg-stage-subtext">
              Your organization requires MFA. Enter your mobile number to receive a verification code.
            </p>
            <div className="form-phone-wrapper">
              <select
                className="form-phone-country"
                value={selectedCountry.code}
                onChange={(e) => {
                  const selected = COUNTRY_MAP.get(e.target.value as CountryCode);
                  if (selected) {
                    setSelectedCountry(selected);
                    setPhoneDisplay('');
                    digitsRef.current = '';
                    setPhoneNumber('');
                  }
                }}
              >
                <option key="selected" hidden value={selectedCountry.code}>
                  {selectedCountry.code} {selectedCountry.dial_code}
                </option>
                {COUNTRY_OPTIONS.map((c) => (
                  <option key={c.code} value={c.code}>{c.name} {c.dial_code}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="Phone Number"
                inputMode="tel"
                autoComplete="off"
                ref={phoneInputRef}
                value={phoneDisplay}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                onPaste={(e) => e.preventDefault()}
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <div className="button-group">
              <button type="submit" disabled={loading} className="registrationLoginBtn">
                <span className="invisible-placeholder">Send Code</span>
                <span className="button-content">Send Code</span>
              </button>
            </div>
          </form>
        )}

        {/* ── Verify Mobile ────────────────────────────────────────────────── */}
        {stage === FormStage.VerifyMobile && (
          <div className="reg-stage-form">
            <h2>Verify your number</h2>
            <p className="reg-stage-subtext">
              We sent a 6-digit code to <strong>{phoneNumber}</strong>.
            </p>
            <input
              className="reg-otp-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="——————"
              maxLength={6}
              value={phoneOtp}
              onChange={(e) => handlePhoneOtpChange(e.target.value)}
              disabled={loading}
              autoFocus
            />
            {error && <div className="error-message">{error}</div>}
            <div className="reg-resend-text">
              {phoneResendCountdown > 0 ? (
                <span className="reg-resend-countdown">Resend in {phoneResendCountdown}s</span>
              ) : (
                <span className="reg-resend-link" onClick={handleResendPhone}>Resend code</span>
              )}
            </div>
          </div>
        )}

        {/* ── Password ─────────────────────────────────────────────────────── */}
        {stage === FormStage.Password && (
          <form className="reg-stage-form" onSubmit={handlePasswordSubmit} autoComplete="off">
            <h2>Set a password</h2>
            <p className="reg-stage-subtext">
              {isOrg
                ? "Set a password for your account — you'll customize your org on the next step."
                : 'Almost done! Set your password to complete registration.'}
            </p>
            <div className="password-tooltip-wrapper">
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  aria-label="Toggle password visibility"
                  className="toggle-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <EyeIcon visible={showPassword} />
                </button>
              </div>
              <span
                className="tooltip-icon"
                onClick={() => setShowPasswordTooltip((v) => !v)}
              >
                ⚠️
              </span>
            </div>
            <div className="password-field">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                aria-label="Toggle confirm password visibility"
                className="toggle-visibility"
                onClick={() => setShowConfirmPassword((v) => !v)}
              >
                <EyeIcon visible={showConfirmPassword} />
              </button>
            </div>
            {showPasswordTooltip && (
              <div className="tooltip">
                Password must be ≥8 characters and include at least one uppercase letter, one number, and one special character.
              </div>
            )}
            {!isOrg && (
              <label className="terms-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                />
                I agree to the{' '}
                <TermsAndConditionsLink isCaddieGames={isCaddieGames} />
              </label>
            )}
            {error && <div className="error-message">{error}</div>}
            <div className="button-group">
              <button type="submit" disabled={loading} className="registrationLoginBtn">
                <span className="invisible-placeholder">
                  {isOrg ? 'Continue' : 'Complete Registration'}
                </span>
                <span className="button-content">
                  {isOrg ? 'Continue' : 'Complete Registration'}
                </span>
              </button>
            </div>
          </form>
        )}

        {/* ── Customization ────────────────────────────────────────────────── */}
        {stage === FormStage.Customization && (
          <form className="reg-stage-form" onSubmit={handleCustomizationSubmit} autoComplete="off">
            <h2>Your Organization</h2>
            <input
              className="org-details-input-field"
              type="text"
              placeholder="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
            <input
              className="org-details-input-field"
              type="text"
              placeholder="Legal Entity Name"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              required
            />
            <input
              className="org-details-input-field"
              type="text"
              placeholder="Banner Name (e.g. AskBob)"
              maxLength={20}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="colour-picker-title">Banner Colours:</p>
            <div className="new-org-color-picker-row">
              <div className="new-org-color-picker-group">
                <div className="new-org-color-picker-label">Banner</div>
                <ColorWheelPicker
                  value={colour}
                  ariaLabel="Banner colour"
                  presets={['#218838', '#4B0000', '#2F5597']}
                  onChange={(hex) => setColour(hex)}
                />
              </div>
              <div className="new-org-color-picker-group">
                <div className="new-org-color-picker-label">Text</div>
                <ColorWheelPicker
                  value={textColour}
                  ariaLabel="Banner text colour"
                  onChange={(hex) => setTextColour(hex)}
                />
              </div>
              <div className="new-org-color-picker-group">
                <div className="new-org-color-picker-label">Glow</div>
                <ColorWheelPicker
                  glow
                  value={glow}
                  ariaLabel="Banner glow"
                  onChange={setGlow}
                />
              </div>
            </div>
            <label className="terms-label">
              <input
                type="checkbox"
                checked={orgTermsAccepted}
                onChange={(e) => setOrgTermsAccepted(e.target.checked)}
                required
              />
              I agree to the{' '}
              <TermsAndConditionsLink isCaddieGames={isCaddieGames} />
            </label>
            {error && <div className="error-message">{error}</div>}
            <div className="button-group">
              <button type="submit" disabled={loading} className="registrationLoginBtn">
                <span className="invisible-placeholder">Create Organization</span>
                <span className="button-content">Create Organization</span>
              </button>
            </div>
          </form>
        )}

      </div>

      <SmsConsentModal
        show={showSmsConsent}
        onCancel={() => setShowSmsConsent(false)}
        onAgree={handleSmsConsentAgree}
        disabled={loading}
      />

      {showExistingPopup && (
        <div className="popup-overlay">
          <div className="existing-user-popup-menu">
            <p onClick={() => navigate('/existing/user')}>Join another Organization.</p>
            <p onClick={() => navigate('/existing/org')}>Create a new Organization</p>
            <button className="existing-user-cancel-btn" onClick={() => setShowExistingPopup(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
